import { memo, useState } from 'react';
import {
  Stack,
  IconButton,
  Drawer,
  Box,
  Typography,
  List,
  ListItem,
  ListItemButton,
  Collapse,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { useRouter } from '@/routes/hooks/useRouter';
import type { Menu } from '@/models/Menu';
import { SearchIcon } from '@/assets/icons';

interface MobileMenuProps {
  handleParentClick: (menu: Menu) => void;
  handleChildClick: (menu: Menu) => void;
  data: Menu[] | undefined;
}

const MobileMenu = (props: MobileMenuProps) => {
  const { handleParentClick, handleChildClick, data } = props;
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [mobileExpandedMenuId, setMobileExpandedMenuId] = useState<number | null>(null);
  const router = useRouter();
  const theme = useTheme();
  const lgDown = useMediaQuery(theme.breakpoints.down('lg'));
  const toggleDrawer = (open: boolean) => (event: React.KeyboardEvent | React.MouseEvent) => {
    if (
      event.type === 'keydown' &&
      ((event as React.KeyboardEvent).key === 'Tab' ||
        (event as React.KeyboardEvent).key === 'Shift')
    ) {
      return;
    }
    setDrawerOpen(open);
  };

  const handleMobileMenuExpand = (id: number) => {
    setMobileExpandedMenuId(mobileExpandedMenuId === id ? null : id);
  };

  return (
    <>
      {/* Mobile Menu Icon */}
      <Stack direction='row' alignItems='center' sx={{ display: lgDown ? 'flex' : 'none' }}>
        <IconButton
          color='inherit'
          aria-label='open drawer'
          edge='end'
          onClick={toggleDrawer(true)}
          sx={{ color: 'text.primary' }}
        >
          <MenuIcon />
        </IconButton>
      </Stack>

      {/* Mobile Drawer */}
      <Drawer
        anchor='right'
        open={drawerOpen}
        onClose={toggleDrawer(false)}
        slotProps={{
          paper: {
            sx: { width: '100%', maxWidth: '300px', p: 2 },
          },
        }}
      >
        <Box display='flex' justifyContent='space-between' alignItems='center' mb={2}>
          <Typography variant='h6' sx={{ fontWeight: 'bold' }}>
            القائمة
          </Typography>
          <IconButton onClick={toggleDrawer(false)}>
            <CloseIcon />
          </IconButton>
        </Box>
        <List>
          <ListItem disablePadding>
            <ListItemButton
              onClick={() => {
                router.push('HOME');
                setDrawerOpen(false);
              }}
            >
              <Typography sx={{ fontWeight: 600 }}>الرئيسية</Typography>
            </ListItemButton>
          </ListItem>

          {data?.map((menu) => (
            <Box key={`${menu.id}-mobile-menu`}>
              <ListItem disablePadding>
                <ListItemButton>
                  <Box
                    display='flex'
                    justifyContent='space-between'
                    alignItems='center'
                    width='100%'
                  >
                    <Typography
                      sx={{ fontWeight: 600, width: '12rem', height: '2rem' }}
                      onClick={() => {
                        handleParentClick(menu);
                        setDrawerOpen(false);
                      }}
                    >
                      {menu.menuDetail.title}
                    </Typography>
                    {menu.children && menu.children.length > 0 && (
                      <KeyboardArrowDownIcon
                        sx={{
                          height: '2rem',
                          width: '2rem',
                          transform:
                            mobileExpandedMenuId === menu.id ? 'rotate(180deg)' : 'rotate(0deg)',
                          transition: 'transform 0.3s',
                        }}
                        onClick={() => {
                          handleMobileMenuExpand(menu.id);
                        }}
                      />
                    )}
                  </Box>
                </ListItemButton>
              </ListItem>
              {menu.children && menu.children.length > 0 && (
                <Collapse in={mobileExpandedMenuId === menu.id} timeout='auto' unmountOnExit>
                  <List component='div' disablePadding sx={{ pl: 2 }}>
                    {menu.children.map((childMenu) => (
                      <ListItem key={`${childMenu.id}-mobile-child`} disablePadding>
                        <ListItemButton
                          onClick={() => {
                            handleChildClick(childMenu);
                            setDrawerOpen(false);
                          }}
                        >
                          <Typography sx={{ fontSize: '0.9rem' }}>
                            {childMenu.menuDetail.title}
                          </Typography>
                        </ListItemButton>
                      </ListItem>
                    ))}
                  </List>
                </Collapse>
              )}
            </Box>
          ))}
          <ListItem disablePadding>
            <ListItemButton
              onClick={() => {
                router.push('SEARCH_RESULTS');
                setDrawerOpen(false);
              }}
              sx={{ gap: '1rem' }}
            >
              <Typography sx={{ fontWeight: 600 }}>البحث</Typography>
              <SearchIcon />
            </ListItemButton>
          </ListItem>
        </List>
      </Drawer>
    </>
  );
};

export default memo(MobileMenu);
